import React, { useMemo } from "react";

import {
    getARPlatform,
    getHostedARModelUrls,
    looksLikeUsdzUrl,
    openAndroidSceneViewer,
} from "../../services/arViewer.service";

const pageStyle = {
    minHeight: "100vh",
    background: "#f5f7f6",
    padding: "20px 14px 30px",
    fontFamily: "Barlow, sans-serif",
};

const cardStyle = {
    maxWidth: "520px",
    margin: "0 auto",
    background: "#ffffff",
    borderRadius: "18px",
    border: "1px solid #dfe5df",
    boxShadow: "0 10px 35px rgba(17,24,39,0.08)",
    padding: "28px 20px",
};

const primaryBtn = {
    display: "block",
    width: "100%",
    borderRadius: "10px",
    border: "1px solid #1fac48",
    background: "#1fac48",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "17px",
    padding: "16px",
    cursor: "pointer",
    marginTop: "16px",
    textAlign: "center",
    textDecoration: "none",
};

const parseHashQuery = () => {
    if (typeof window === "undefined") return new URLSearchParams();
    const hash = window.location.hash || "";
    const queryString = hash.includes("?") ? hash.split("?")[1] : "";
    return new URLSearchParams(queryString);
};

/* ─── iOS section ────────────────────────────────────────────────────── */
const IosSection = ({ iosModelUrl, buildingTypeName, width, length, height }) => {
    const isValidUsdz = looksLikeUsdzUrl(iosModelUrl);

    if (!isValidUsdz) {
        return (
            <div style={{ marginTop: 16, padding: "14px", borderRadius: 10, background: "#fff7f7", border: "1px solid #efc7c7", color: "#9f1239", fontSize: 14 }}>
                No USDZ model URL is configured for iPhone Quick Look yet.
            </div>
        );
    }

    return (
        <>
            <p style={{ color: "#4b5563", fontSize: 15, lineHeight: 1.6, marginTop: 14 }}>
                Tap below to place <strong>{buildingTypeName}</strong>
                {(width || length || height) ? ` (${width}W × ${length}L × ${height}H)` : ""} in your real environment using Apple Quick Look AR.
            </p>

            {/*
              * IMPORTANT: iOS Quick Look requires a direct native tap on <a rel="ar">.
              * Any JS .click() or async wrapper loses the user-gesture context and
              * causes the camera to open and immediately close.
              */}
            <a rel="ar" href={iosModelUrl} style={primaryBtn}>
                {/* <img> child is required by the iOS Quick Look spec */}
                <img
                    src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="
                    alt=""
                    style={{ display: "none" }}
                />
                📱 View in AR (Quick Look)
            </a>
        </>
    );
};

/* ─── Android section ────────────────────────────────────────────────── */
const AndroidSection = ({ androidModelUrl, buildingTypeName, width, length, height }) => {
    const [error, setError] = React.useState("");

    const launch = () => {
        const result = openAndroidSceneViewer(androidModelUrl, buildingTypeName || "Sensei Building");
        if (!result?.ok) setError(result?.reason || "Could not open Scene Viewer.");
    };

    return (
        <>
            <p style={{ color: "#4b5563", fontSize: 15, lineHeight: 1.6, marginTop: 14 }}>
                Tap below to place <strong>{buildingTypeName}</strong>
                {(width || length || height) ? ` (${width}W × ${length}L × ${height}H)` : ""} in your real environment using Google Scene Viewer AR.
            </p>
            <button type="button" style={primaryBtn} onClick={launch}>
                📱 View in AR (Scene Viewer)
            </button>
            {error && (
                <div style={{ marginTop: 12, padding: "12px", borderRadius: 10, background: "#fff7f7", border: "1px solid #efc7c7", color: "#9f1239", fontSize: 14 }}>
                    {error}
                </div>
            )}
        </>
    );
};

/* ─── Page ───────────────────────────────────────────────────────────── */
const MobileARLanding = () => {
    const arPlatform = useMemo(() => getARPlatform(), []);
    const query = useMemo(() => parseHashQuery(), []);

    const buildingTypeName = query.get("buildingTypeName") || "Sensei Building";
    const width = query.get("width") || "";
    const length = query.get("length") || "";
    const height = query.get("height") || "";
    const manufacturerId = query.get("manufacturerId") || "";
    const shareLink = query.get("shareLink") || "";

    const arConfig = useMemo(
        () => getHostedARModelUrls({ shareLink, buildingTypeName, width, length, height, manufacturerId }),
        [shareLink, buildingTypeName, width, length, height, manufacturerId]
    );

    return (
        <div style={pageStyle}>
            <div style={cardStyle}>
                <div style={{ textAlign: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 56 }}>🏗️</div>
                    <h1 style={{ margin: "10px 0 0", fontSize: 26, fontWeight: 700, color: "#101828" }}>
                        View In Your Space
                    </h1>
                </div>

                {arPlatform === "ios" && (
                    <IosSection
                        iosModelUrl={arConfig.iosModelUrl}
                        buildingTypeName={buildingTypeName}
                        width={width}
                        length={length}
                        height={height}
                    />
                )}

                {arPlatform === "android" && (
                    <AndroidSection
                        androidModelUrl={arConfig.androidModelUrl}
                        buildingTypeName={buildingTypeName}
                        width={width}
                        length={length}
                        height={height}
                    />
                )}

                {arPlatform === "desktop" && (
                    <p style={{ color: "#6b7280", fontSize: 15, marginTop: 16, textAlign: "center" }}>
                        Open this page on an iPhone or Android device to launch the AR viewer.
                    </p>
                )}
            </div>
        </div>
    );
};

export default MobileARLanding;
